import * as THREE from "three";
import {
    enforceTeethDuctBlack,
    isTeethDuctMesh,
} from "./DuctMaterialGuards.js";

/**
 * TeethMaterialManager
 * Manages material properties for teeth meshes, including metallicness control
 */
export class TeethMaterialManager {
    constructor() {
        this.teethMeshes = new Map(); // Store teeth meshes and their materials
        this.originalMaterials = new Map(); // Store original material properties
        this.isActive = false;

        // Default teeth material configuration
        this.config = {
            metallicness: 0.0, // Default 0% metallicness for realistic teeth
            roughness: 1.0, // Moderate roughness for natural tooth texture
            color: 0xa8a8a8, // White color for teeth
            opacity: 1.0, // Full opacity
            transparent: false, // No transparency by default
            envMapIntensity: 0.5, // Moderate environment reflection
            clearcoat: 0.2, // Slight clearcoat for enamel effect
            clearcoatRoughness: 0.1, // Smooth clearcoat
        };

        // Different teeth material presets
        this.presets = {
            default: {
                metallicness: 0.0,
                roughness: 0.3,
                color: 0xa8a8a8,
                envMapIntensity: 0.5,
                clearcoat: 0.1,
                clearcoatRoughness: 0.1,
            },
            realistic: {
                metallicness: 0.0,
                roughness: 0.3,
                color: 0xa8a8a8,
                envMapIntensity: 0.5,
                clearcoat: 0.2,
                clearcoatRoughness: 0.1,
            },
            ceramic: {
                metallicness: 0.0,
                roughness: 0.1,
                color: 0xa8a8a8,
                envMapIntensity: 0.7,
                clearcoat: 0.4,
                clearcoatRoughness: 0.05,
            },
            matte: {
                metallicness: 0.0,
                roughness: 0.8,
                color: 0xf5f5f5,
                envMapIntensity: 0.2,
                clearcoat: 0.0,
                clearcoatRoughness: 0.0,
            },
            glossy: {
                metallicness: 0.1,
                roughness: 0.1,
                color: 0xffffff,
                envMapIntensity: 0.9,
                clearcoat: 0.6,
                clearcoatRoughness: 0.05,
            },
        };

        console.log("TeethMaterialManager initialized");
    }

    /**
     * Initialize the teeth material system by finding teeth meshes
     */
    initialize(model) {
        if (!model) {
            console.warn("No model provided to TeethMaterialManager");
            return;
        }

        console.log("Initializing teeth material management system...");

        // Find all teeth meshes
        model.traverse((child) => {
            if (child.isMesh) {
                const meshName = child.name.toLowerCase();

                // Check if this is a teeth mesh
                // "teethDucthead" is an internal facial duct, not tooth
                // enamel. Its authored black material must never be replaced
                // by this manager's whitening preset.
                const isTeethMesh =
                    meshName.includes("teeth") && !isTeethDuctMesh(child);

                if (isTeethMesh) {
                    console.log("Found teeth mesh:", child.name);
                    this.setupTeethMesh(child);
                }
            }
        });

        this.isActive = true;

        // Apply default material configuration
        this.applyConfiguration(this.config);

        // Keep a correctly named duct black even if an authored GLB arrives
        // with a stray color. ModelViewer repeats this after mouth setup so
        // both broad managers have finished before the scene is displayed.
        enforceTeethDuctBlack(model);

        console.log(
            `Teeth material manager initialized with ${this.teethMeshes.size} teeth meshes`,
        );
    }

    /**
     * Set up a teeth mesh for material management
     */
    setupTeethMesh(mesh) {
        // Store original material properties
        const originalMaterial = mesh.material;
        this.originalMaterials.set(mesh.uuid, {
            metallicness: originalMaterial.metalness || 0.0,
            roughness: originalMaterial.roughness || 0.5,
            color: originalMaterial.color
                ? originalMaterial.color.clone()
                : new THREE.Color(0xffffff),
            opacity: originalMaterial.opacity || 1.0,
            transparent: originalMaterial.transparent || false,
            envMapIntensity: originalMaterial.envMapIntensity || 1.0,
            clearcoat: originalMaterial.clearcoat || 0.0,
            clearcoatRoughness: originalMaterial.clearcoatRoughness || 0.0,
        });

        // Create enhanced material for teeth
        const enhancedMaterial =
            this.createEnhancedTeethMaterial(originalMaterial);
        mesh.material = enhancedMaterial;

        // Store mesh reference
        this.teethMeshes.set(mesh.uuid, {
            mesh: mesh,
            originalMaterial: originalMaterial,
            enhancedMaterial: enhancedMaterial,
        });

        console.log("Set up teeth mesh for material management:", mesh.name);
    }

    /**
     * Create an enhanced material for teeth
     */
    createEnhancedTeethMaterial(originalMaterial) {
        let enhancedMaterial;

        if (originalMaterial.isMeshStandardMaterial) {
            enhancedMaterial = new THREE.MeshStandardMaterial({
                map: originalMaterial.map,
                normalMap: originalMaterial.normalMap,
                roughnessMap: originalMaterial.roughnessMap,
                metalnessMap: originalMaterial.metalnessMap,
                aoMap: originalMaterial.aoMap,
                emissiveMap: originalMaterial.emissiveMap,
                color: originalMaterial.color
                    ? originalMaterial.color.clone()
                    : new THREE.Color(0xffffff),
                roughness: originalMaterial.roughness || 0.5,
                metalness: originalMaterial.metalness || 0.0,
                transparent: originalMaterial.transparent || false,
                opacity: originalMaterial.opacity || 1.0,
                side: originalMaterial.side || THREE.FrontSide,
                envMapIntensity: originalMaterial.envMapIntensity || 1.0,
            });
        } else {
            // Create new standard material from other material types
            enhancedMaterial = new THREE.MeshStandardMaterial({
                color: originalMaterial.color
                    ? originalMaterial.color.clone()
                    : new THREE.Color(0xffffff),
                map: originalMaterial.map,
                roughness: 0.3,
                metalness: 0.0,
                transparent: originalMaterial.transparent || false,
                opacity: originalMaterial.opacity || 1.0,
                side: originalMaterial.side || THREE.FrontSide,
            });
        }

        // Mark as teeth material
        enhancedMaterial.userData.isTeethMaterial = true;

        return enhancedMaterial;
    }

    /**
     * Set metallicness for all teeth meshes
     */
    setMetallicness(value) {
        const clampedValue = Math.max(0.0, Math.min(1.0, value));
        this.config.metallicness = clampedValue;

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                enhancedMaterial.metalness = clampedValue;
                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log(
            `Set teeth metallicness to ${(clampedValue * 100).toFixed(1)}%`,
        );
    }

    /**
     * Set roughness for all teeth meshes
     */
    setRoughness(value) {
        const clampedValue = Math.max(0.0, Math.min(1.0, value));
        this.config.roughness = clampedValue;

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                enhancedMaterial.roughness = clampedValue;
                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log(
            `Set teeth roughness to ${(clampedValue * 100).toFixed(1)}%`,
        );
    }

    /**
     * Set color for all teeth meshes
     */
    setColor(color) {
        const teethColor = new THREE.Color(color);
        this.config.color = teethColor.getHex();

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                enhancedMaterial.color.copy(teethColor);
                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log(`Set teeth color to #${teethColor.getHexString()}`);
    }

    /**
     * Set environment map intensity for all teeth meshes
     */
    setEnvMapIntensity(value) {
        const clampedValue = Math.max(0.0, Math.min(2.0, value));
        this.config.envMapIntensity = clampedValue;

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                enhancedMaterial.envMapIntensity = clampedValue;
                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log(
            `Set teeth environment map intensity to ${clampedValue.toFixed(2)}`,
        );
    }

    /**
     * Set clearcoat properties for all teeth meshes
     */
    setClearcoat(clearcoat, clearcoatRoughness = null) {
        const clampedClearcoat = Math.max(0.0, Math.min(1.0, clearcoat));
        this.config.clearcoat = clampedClearcoat;

        if (clearcoatRoughness !== null) {
            const clampedRoughness = Math.max(
                0.0,
                Math.min(1.0, clearcoatRoughness),
            );
            this.config.clearcoatRoughness = clampedRoughness;
        }

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                enhancedMaterial.clearcoat = clampedClearcoat;
                if (clearcoatRoughness !== null) {
                    enhancedMaterial.clearcoatRoughness =
                        this.config.clearcoatRoughness;
                }
                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log(
            `Set teeth clearcoat to ${(clampedClearcoat * 100).toFixed(1)}%`,
        );
        if (clearcoatRoughness !== null) {
            console.log(
                `Set teeth clearcoat roughness to ${(this.config.clearcoatRoughness * 100).toFixed(1)}%`,
            );
        }
    }

    /**
     * Apply a complete material configuration
     */
    applyConfiguration(config) {
        Object.assign(this.config, config);

        this.teethMeshes.forEach(({ enhancedMaterial }) => {
            if (enhancedMaterial.userData.isTeethMaterial) {
                if (config.metallicness !== undefined) {
                    enhancedMaterial.metalness = config.metallicness;
                }
                if (config.roughness !== undefined) {
                    enhancedMaterial.roughness = config.roughness;
                }
                if (config.color !== undefined) {
                    enhancedMaterial.color.setHex(config.color);
                }
                if (config.opacity !== undefined) {
                    enhancedMaterial.opacity = config.opacity;
                }
                if (config.transparent !== undefined) {
                    enhancedMaterial.transparent = config.transparent;
                }
                if (config.envMapIntensity !== undefined) {
                    enhancedMaterial.envMapIntensity = config.envMapIntensity;
                }
                if (config.clearcoat !== undefined) {
                    enhancedMaterial.clearcoat = config.clearcoat;
                }
                if (config.clearcoatRoughness !== undefined) {
                    enhancedMaterial.clearcoatRoughness =
                        config.clearcoatRoughness;
                }

                enhancedMaterial.needsUpdate = true;
            }
        });

        console.log("Applied teeth material configuration:", config);
    }

    /**
     * Apply a material preset
     */
    applyPreset(presetName) {
        if (this.presets[presetName]) {
            this.applyConfiguration(this.presets[presetName]);
            console.log(`Applied teeth material preset: ${presetName}`);
        } else {
            console.warn(`Unknown teeth material preset: ${presetName}`);
            console.log("Available presets:", Object.keys(this.presets));
        }
    }

    /**
     * Restore original material properties
     */
    restoreOriginalMaterials() {
        this.teethMeshes.forEach(({ mesh, originalMaterial }, uuid) => {
            const originalProps = this.originalMaterials.get(uuid);
            if (originalProps && mesh.material.userData.isTeethMaterial) {
                mesh.material.metalness = originalProps.metallicness;
                mesh.material.roughness = originalProps.roughness;
                mesh.material.color.copy(originalProps.color);
                mesh.material.opacity = originalProps.opacity;
                mesh.material.transparent = originalProps.transparent;
                mesh.material.envMapIntensity = originalProps.envMapIntensity;
                mesh.material.clearcoat = originalProps.clearcoat;
                mesh.material.clearcoatRoughness =
                    originalProps.clearcoatRoughness;
                mesh.material.needsUpdate = true;
            }
        });

        console.log("Restored original teeth material properties");
    }

    /**
     * Get current material configuration
     */
    getCurrentConfiguration() {
        return { ...this.config };
    }

    /**
     * Get list of available presets
     */
    getAvailablePresets() {
        return Object.keys(this.presets);
    }

    /**
     * Get current status for debugging
     */
    getStatus() {
        return {
            isActive: this.isActive,
            teethMeshCount: this.teethMeshes.size,
            currentConfig: this.config,
            availablePresets: this.getAvailablePresets(),
        };
    }

    /**
     * Clean up resources
     */
    dispose() {
        // Restore original materials
        this.restoreOriginalMaterials();

        this.teethMeshes.clear();
        this.originalMaterials.clear();
        this.isActive = false;

        console.log("TeethMaterialManager disposed");
    }
}
